import { collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, updateDoc, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functionsClient } from "../firebase";

const CANONICAL_ROLES = new Set(["superadmin", "admin", "coach", "member", "investor"]);

function toMillis(value) {
  if (!value) return null;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeRole(value) {
  const role = String(value || "member").trim().toLowerCase();
  return CANONICAL_ROLES.has(role) ? role : "member";
}

function parseTimestampFromId(value) {
  const raw = String(value || "");
  const match = raw.match(/_(\d{10,14})$/);
  if (!match?.[1]) return null;
  const n = Number(match[1]);
  if (!Number.isFinite(n)) return null;
  return match[1].length <= 10 ? n * 1000 : n;
}

export async function fetchOrgUsers(user, orgId) {
  if (!user?.uid) return [];
  try {
    const snap = await getDocs(collection(db, "orgs", orgId, "users"));
    const users = snap.docs.map((docSnap) => {
      const data = docSnap.data() || {};
      const createdAt =
        toMillis(data.createdAt) ||
        toMillis(data.joinedAt) ||
        toMillis(data.linkedAt) ||
        parseTimestampFromId(docSnap.id) ||
        null;
      const hasOnboardingEmailSent = typeof data.onboardingEmailSent === "boolean";
      const hasLastSignInTime =
        Object.prototype.hasOwnProperty.call(data, "lastSignInTime") ||
        Object.prototype.hasOwnProperty.call(data, "lastLoginAt");
      const hasLinkNotificationSent = typeof data.linkNotificationSent === "boolean";
      const linkedAt = toMillis(data.linkedAt) || null;
      const isLinkedUser = Boolean(linkedAt) || Boolean(data.linkedFromExistingAuth);
      return {
        id: docSnap.id,
        ...data,
        role: normalizeRole(data.role),
        createdAt,
        linkedAt,
        isLinkedUser,
        onboardingEmailSent: hasOnboardingEmailSent ? Boolean(data.onboardingEmailSent) : null,
        hasOnboardingEmailSent,
        linkNotificationSent: hasLinkNotificationSent ? Boolean(data.linkNotificationSent) : null,
        hasLinkNotificationSent,
        lastSignInTime: data.lastSignInTime || data.lastLoginAt || "",
        hasLastSignInTime,
      };
    });
    return users;
  } catch {
    return [];
  }
}

export async function findOrgUserForAccount(user, orgId) {
  if (!user?.uid || !orgId) return null;
  try {
    const byId = await getDocs(query(collection(db, "orgs", orgId, "users"), where("id", "==", user.uid)));
    if (!byId.empty) {
      const docSnap = byId.docs[0];
      return { id: docSnap.id, ...docSnap.data() };
    }
  } catch {}

  if (!user?.email) return null;
  try {
    const byEmail = await getDocs(query(collection(db, "orgs", orgId, "users"), where("email", "==", user.email)));
    if (!byEmail.empty) {
      const docSnap = byEmail.docs[0];
      return { id: docSnap.id, ...docSnap.data() };
    }
  } catch {}
  return null;
}

export async function getCanonicalOrgMembershipForAccount(user, orgId) {
  if (!user?.uid || !orgId) return null;
  const membership = await getDoc(
    doc(db, "orgs", orgId, "users", user.uid)
  );
  if (!membership.exists()) return null;
  return {
    ...(membership.data() || {}),
    id: membership.id,
  };
}

export async function syncOrgUserForAccount(user, orgId, updates = {}) {
  if (!user?.uid || !orgId) return false;
  const existing = await findOrgUserForAccount(user, orgId);
  const payload = {
    ...updates,
    updatedAt: Date.now(),
  };
  try {
    if (existing?.id) {
      await setDoc(doc(db, "orgs", orgId, "users", existing.id), payload, { merge: true });
    } else {
      await setDoc(
        doc(db, "orgs", orgId, "users", user.uid),
        {
          id: user.uid,
          username: user.username || user.email?.split("@")[0] || "Member",
          email: user.email || "",
          role: user.role || "Member",
          status: "active",
          ...payload,
        },
        { merge: true }
      );
    }
    return true;
  } catch {
    return false;
  }
}

export async function saveOrgUser(user, orgId, nextUser) {
  if (!user?.uid || user?.role === "investor") {
    throw new Error("An authorised organisation manager is required.");
  }
  const id = nextUser.id || `${nextUser.username || "user"}_${Date.now()}`;
  const status = String(nextUser.status || "active").trim().toLowerCase();
  const isPending = status === "pending";
  const requestedRole = String(nextUser.role || "member").trim().toLowerCase();
  if (isPending && requestedRole !== "member") {
    throw new Error(
      "Pending invitations support member only; provision privileged roles directly."
    );
  }
  const payload = {
    ...nextUser,
    id,
    role: normalizeRole(nextUser.role),
    status: isPending ? "pending" : "active",
    firstName: String(nextUser.firstName || "").trim(),
    surname: String(nextUser.surname || "").trim(),
    ...(isPending
      ? {}
      : {
          createdAt: nextUser.createdAt || Date.now(),
          onboardingEmailSent:
            typeof nextUser.onboardingEmailSent === "boolean"
              ? nextUser.onboardingEmailSent
              : false,
          lastSignInTime: typeof nextUser.lastSignInTime === "string" ? nextUser.lastSignInTime : "",
        }),
  };
  await setDoc(doc(db, "orgs", orgId, "users", id), payload, { merge: true });
  return true;
}

export async function addSuperadminToOrg(user, orgId) {
  if (!user?.uid || user?.role !== "superadmin") return;
  if (!orgId) return;
  const id = `superadmin_${user.uid}`;
  const payload = {
    id,
    username: user.username || "Superadmin",
    email: user.email || "",
    role: "superadmin",
    addedAt: Date.now(),
  };
  try {
    await setDoc(doc(db, "orgs", orgId, "users", id), payload, { merge: true });
  } catch {}
}

export async function updateOrgUser(user, orgId, userId, updates) {
  if (!user?.uid || user?.role === "investor") return;
  if (!userId) return;
  try {
    await updateDoc(doc(db, "orgs", orgId, "users", userId), updates);
  } catch {}
}

export async function deleteOrgUser(user, orgId, userId) {
  if (!user?.uid || user?.role === "investor") return false;
  if (!userId) return false;
  try {
    await deleteDoc(doc(db, "orgs", orgId, "users", userId));
    return true;
  } catch {
    return false;
  }
}

export async function seedOrgUsers(user, orgId) {
  if (!user?.uid || user?.role === "investor") return;
  const seed = [
    { username: "admin", firstName: "Wesley", surname: "Admin", email: "admin@org.com", role: "Admin" },
    { username: "coach", firstName: "Taylor", surname: "Coach", email: "coach@org.com", role: "Coach" },
    { username: "member", firstName: "Alex", surname: "Member", email: "member@org.com", role: "Member" },
  ];
  await Promise.all(seed.map((u) => saveOrgUser(user, orgId, u)));
}

export async function linkSelfToOrg(user, orgId) {
  if (!user?.uid || !orgId) {
    throw new Error("Authenticated user and organisation are required.");
  }
  const claimOrgInvite = httpsCallable(functionsClient, "claimOrgInvite");
  const result = await claimOrgInvite({ orgId: String(orgId).trim() });
  return result?.data || null;
}
